import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { usePrintNuvemEnvioLabelToast } from 'domains/NuvemEnvioCommons/Orders/hooks/usePrintNuvemEnvioLabelToast';
import 'domains/NuvemEnvioCommons/Orders/utils/print-pdf.scss';
import { NE_LABEL_WIDTH } from 'domains/NuvemEnvioCommons/Orders/utils/print-ne-label-configs';

interface PdfPageProps {
  url: string;
  autoPrint?: boolean;
}

export default function PdfPage({
  url,
  autoPrint = true,
}: Readonly<PdfPageProps>) {
  const { addErrorToast } = usePrintNuvemEnvioLabelToast();
  const [numPages, setNumPages] = useState(0);
  const renderedPagesRef = useRef(0);
  const hasPrintedRef = useRef(false);

  const file = useMemo(() => ({ url }), [url]);

  useEffect(() => {
    renderedPagesRef.current = 0;
    hasPrintedRef.current = false;
    setNumPages(0);
  }, [url]);

  const onDocLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const onDocLoadError = useCallback(
    (err: unknown) => {
      console.error('PDF load error:', err);
      addErrorToast();
    },
    [addErrorToast],
  );

  const onPageRenderSuccess = useCallback(() => {
    renderedPagesRef.current += 1;

    if (
      autoPrint &&
      numPages > 0 &&
      renderedPagesRef.current === numPages &&
      !hasPrintedRef.current
    ) {
      hasPrintedRef.current = true;
      setTimeout(() => globalThis.print(), 0);
    }
  }, [autoPrint, numPages]);

  const onPageRenderError = useCallback(
    (err: unknown) => {
      console.error('PDF page render error:', err);
      addErrorToast();
    },
    [addErrorToast],
  );

  return (
    <div className="pdf-print">
      <Document
        file={file}
        onLoadSuccess={onDocLoadSuccess}
        onLoadError={onDocLoadError}
        onSourceError={onDocLoadError}
        loading={null}
        error={null}
        noData={null}
      >
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => (
          <Page
            key={pageNumber}
            pageNumber={pageNumber}
            width={NE_LABEL_WIDTH}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            onRenderSuccess={onPageRenderSuccess}
            onRenderError={onPageRenderError}
          />
        ))}
      </Document>
    </div>
  );
}
